"use client";

import { TextField, useFormFields } from "@payloadcms/ui";
import { TextFieldClient, TextFieldClientProps } from "payload";
import React, { JSX, useId, useMemo } from "react";

export type SectionBlockProps = {
  blockType: "section";
  sectionTitle: string;
  sectionId?: string;
  headerLevel?: string;
};

type Props = SectionBlockProps & {
  children?: React.ReactNode;
  className?: string;
};

export const SectionBlock: React.FC<Props> = ({
  sectionTitle,
  sectionId,
  headerLevel,
  children,
  className = "",
}: Props) => {
  const id = sectionId || sectionTitle.toLowerCase().replace(/\s+/g, "-");
  const level = headerLevel ? parseInt(headerLevel) : 2;
  const HeaderTag = `h${Math.min(Math.max(level, 1), 6)}` as keyof JSX.IntrinsicElements;

  return (
    <section id={id} className={`content-section ${className}`} data-section-level={level}>
      <HeaderTag>{sectionTitle}</HeaderTag>
      {children}
    </section>
  );
};

export function SectionAdminBlock({ field, path, validate }: TextFieldClientProps) {
  const titleField = useFormFields(([fields]) => fields["sectionTitle"]);
  const idField = useFormFields(([fields]) => fields["sectionId"]);
  const levelField = useFormFields(([fields]) => fields["headerLevel"]);

  const title = titleField?.value as string | undefined;
  const id = idField?.value as string | undefined;
  const level = levelField?.value as string | undefined;

  const instanceId = useId();

  const props: TextFieldClient = useMemo<TextFieldClient>(
    () => ({
      ...field,
      type: "text",
      admin: {
        ...field.admin,
        label: "Section",
        autoComplete: false,
        placeholder: "Section content goes here",
        rtl: false,
      },
    }),
    [field]
  );

  return (
    <>
      <TextField field={props} path={path} validate={validate} />
      {title && (
        <SectionBlockAdmin
          blockType="section"
          sectionTitle={title}
          sectionId={id}
          headerLevel={level}
        />
      )}
    </>
  );
}

export function SectionBlockAdmin({ sectionTitle, sectionId, headerLevel }: SectionBlockProps) {
  const level = headerLevel ? parseInt(headerLevel) : 2;
  const safeLevel = Math.min(Math.max(level, 1), 6);
  const id = sectionId || sectionTitle?.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="section-admin-preview">
      <div className="section-heading">
        H{safeLevel} - {sectionTitle}
      </div>
      <p className="section-id">ID: {id}</p>
      <p className="section-hint">Content will be placed here</p>
      <style>{`
        .section-admin-preview {
          background-color: rgb(36, 36, 36);
          border-radius: 8px;
          padding: 1rem;
          margin: 1rem 0;
          border: 1px solid rgb(76, 76, 76);
        }
        .section-heading {
          font-weight: 600;
          font-size: 1.1rem;
          color: rgb(249, 249, 249);
        }
        .section-id {
          font-size: 0.85rem;
          color: rgb(193, 193, 193);
          margin: 0.5rem 0;
        }
        .section-hint {
          font-style: italic;
          color: rgb(150, 150, 150);
          margin: 0.5rem 0;
          padding-left: 1rem;
          border-left: 2px solid #0051a2;
        }
        @media (max-width: 768px) {
          .content-section {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }
        }
        @media (max-width: 480px) {
          .content-section {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
